import {marked} from 'marked';
import type {ReactElement, ReactNode} from 'react';
import {useRef, useEffect, useState, memo} from 'react';
import {createPortal} from 'react-dom';
import styles from './markdown-renderer.module.scss';
import DOMPurify from 'dompurify';

function MarkdownRenderer({content, suffix}: {content: string; suffix?: ReactNode}): ReactElement {
  const contentRef = useRef<HTMLDivElement>(null);
  const [suffixContainer, setSuffixContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!contentRef.current || !suffix) {
      setSuffixContainer(null);
      return;
    }

    const rafId = requestAnimationFrame(() => {
      if (!contentRef.current) return;

      const paragraphs = contentRef.current.querySelectorAll('p');
      if (paragraphs.length === 0) {
        setSuffixContainer(null);
        return;
      }

      const lastParagraph = paragraphs[paragraphs.length - 1];
      let container = lastParagraph.querySelector<HTMLSpanElement>('.markdown-suffix');

      if (!container) {
        container = document.createElement('span');
        container.className = 'markdown-suffix';
        container.style.display = 'inline';
        container.style.marginLeft = '2px';
        lastParagraph.appendChild(container);
      }

      setSuffixContainer(container);
    });

    return (): void => {
      cancelAnimationFrame(rafId);
    };
  }, [content, suffix]);

  // Strip leading indentation from HTML-tag lines so pretty-printed HTML the agent
  // emits isn't mistaken for a markdown code block (4+ leading spaces) and shown raw.
  // Only lines whose first non-space char is `<` are de-indented, leaving markdown
  // lists (which start with -/*/digits) and prose untouched.
  const deindentedContent = content.replace(/^[ \t]+(?=<)/gm, '');

  const sanitizeConfig = {
    USE_PROFILES: {html: true},
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
  };

  const renderBody = (): ReactElement => {
    const rawHtml = marked.parse(deindentedContent, {async: false});
    return <div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(rawHtml, sanitizeConfig)}} />;
  };

  return (
    <div ref={contentRef} className={styles.content}>
      {renderBody()}

      {suffixContainer && suffix ? createPortal(suffix, suffixContainer) : null}
    </div>
  );
}

export default memo(MarkdownRenderer);
